import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail } from '../../../../helpers/authValidation';
import RoleService from '../../../../services/RoleService/RoleService';
import UserService from '../../../../services/UserService/UserService';
import SettingsService from '../../../../services/SettingsService/SettingsService';
import FormInputs from '../parts/formComponents/FormInputs';
import FormButtons from '../parts/formComponents/FormButtons';
import Validations from '../parts/formComponents/passwordValidations';
import { useNavigate } from "react-router-dom"
import Roles from '../parts/formComponents/Roles';
import { useSetRecoilState } from 'recoil';
import { alertState } from '../../../../recoil-store/recoil-state';
import './AddNewUser.css';

function AddNewUserPage(props) {
    const { t } = useTranslation();
    const history = useNavigate();
    // To update message and button
    const [disableCreateButton, setDisableCreateButton] = useState(true);
    // To update TransferList
    const [, setRoleLeft] = useState([]);
    const [isNewRole, setIsNewRole] = useState(false);
    const roleLeft = useRef([]);
    const roleRight = useRef([]);
    const label = 'CONTENT.USER.';

    // To update input values
    const [values, setValues] = useState({
        first_name: '',
        second_name: '',
        last_name: '',
        user_name: '',
        user_email: '',
        confirmEmail: '',
        user_password: '',
        user_password_retry: ''
    });
    const [validations, setValidations] = useState({
        first_name: false,
        last_name: false,
        user_name: true,
        user_email: false,
        confirmEmail: false,
        user_password: false,
        user_password_retry: false
    });
    const setAlert = useSetRecoilState(alertState);

    useEffect(() => {
        RoleService.getRoles()
            .then(response => {
                roleLeft.current = response.data.data;
                setRoleLeft(response.data.data);
            })
            .catch(err => {
                console.log(err);
            });
    }, [isNewRole]);

    const handleChange = (name, event) => {
        event.preventDefault();
        let value = event.target.value;
        setValues({ ...values, [name]: value });
        if (name === 'first_name') {
            setValidations(oldState => {
                return { ...oldState, first_name: value.trim().length > 1 };
            });
        } else if (name === 'last_name') {
            setValidations(oldState => {
                return { ...oldState, last_name: value.trim().length > 1 };
            });
        } else if (name === 'second_name') {
            setValidations(oldState => {
                return { ...oldState, second_name: true };
            });
            if (value.trim().length === 1) {
                setValidations(oldState => {
                    return { ...oldState, second_name: false };
                });
            }
        } else if (name === 'user_name') {
            setValidations(oldState => {
                return { ...oldState, user_name: value.trim().length > 1 };
            });
        } else if (name === 'user_email') {
            setValidations(oldState => {
                return { ...oldState, user_email: validateEmail(value) };
            });
            setValidations(oldState => {
                return { ...oldState, confirmEmail: (values.confirmEmail === value) };
            });
        } else if (name === 'confirmEmail') {
            setValidations(oldState => {
                return { ...oldState, confirmEmail: (values.user_email === value) };
            });
        } else if (name === 'user_password') {
            setValidations(oldState => {
                return { ...oldState, user_password: Validations.passwordValidations(value) };
            });
            setValidations(oldState => {
                return { ...oldState, user_password_retry: (values.user_password_retry === value) };
            });
        } else if (name === 'user_password_retry') {
            setValidations(oldState => {
                return { ...oldState, user_password_retry: (values.user_password === value) };
            });
        }
        setValidations(currentState => {
            setDisableCreateButton(!Validations.allTrue(currentState));
            return currentState;
        });
    };

    const checkValidForm = () => {
        let isValid = true;
        if (values.first_name.length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.USER.FIRST-NAME-LENGTH-ERROR'),
                severity: 'error'
            }));
            isValid = false;
        } else if (values.last_name.length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.USER.LAST-NAME-LENGTH-ERROR'),
                severity: 'error'
            }));
            isValid = false;
        } else if (values.user_name.length < 3) {
            setAlert(alert => ({
                ...alert,
                show: true,
                message: t('MESSAGES.USER.USER-NAME-LENGTH-ERROR'),
                severity: 'error'
            }));
            isValid = false;
        }
        return isValid;
    }

    const handleCreateUser = async (event) => {
        event.preventDefault();
        if (roleRight.current.length) {
            if (!checkValidForm()) {
                return;
            }
            const response = await UserService.postUser(values);
            if (response) {
                if (response.status) {
                    await SettingsService.postPersonallization({
                        user_id: response.id,
                        date_format: "dd/MM/yyyy",
                        pref_language: "en",
                        pref_theme: "light"
                    });
                    const roles = [];
                    for (let role of roleRight.current) {
                        roles.push(role.id);
                    }
                    const data = {
                        user_role: [{
                            userId: response.id,
                            willBeAddedRole: roles,
                            willBeDeletedRole: []
                        }]
                    };
                    const roleResponse = await RoleService.updateUserRoles(data);
                    if (roleResponse) {
                        setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t(label + response.message),
                            severity: roleResponse.status ? 'success' : 'error'
                        }));
                        if (roleResponse.status) {
                            history('/management/user-management/users');
                        }
                    } else {
                        setAlert(alert => ({
                            ...alert,
                            show: true,
                            message: t('GENERAL.NETWORK-ERROR'),
                            severity: 'error'
                        }));
                    }
                } else {
                    setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t(label + response.message),
                        severity: 'error'
                    }));
                }
            } else {
                setAlert(alert => ({ ...alert, show: true, message: t('GENERAL.NETWORK-ERROR'), severity: 'error' }));
            }
        } else {
            setAlert(alert => ({ ...alert, show: true, message: t('MESSAGES.USER.MIN-ROLE-ERROR'), severity: 'error' }));
        }
    };

    const handleCancel = () => {
        setValidations({
            first_name: false,
            last_name: false,
            user_name: false,
            user_email: false,
            confirmEmail: false,
            user_password: false,
            user_password_retry: false
        });
        setValues({
            first_name: '',
            second_name: '',
            last_name: '',
            user_name: '',
            user_email: '',
            confirmEmail: '',
            user_password: '',
            user_password_retry: ''
        });
        setDisableCreateButton(true);
        Validations.passwordValidations('');
    };

    return (
        <div className="addnewuser-container" data-test="addnewuser-page">
            <div><h3>{t(label + 'ACCOUNT-INFO')}</h3></div>
            <FormInputs
                data-test="form-inputs"
                handleChange={handleChange}
                values={values} validations={validations}
            />
            <div className="hru-container" />
            <div><h3>{t(label + 'ROLE-INFO')}</h3></div>
            <Roles roles={roleLeft} rolesChecked={roleRight} setRoleState={setIsNewRole} />
            <FormButtons
                data-test="form-buttons"
                handleCancel={handleCancel}
                handleCreateUser={handleCreateUser}
                disableCreateButton={disableCreateButton}
                buttonText={t(label + 'CREATE-USER')}
            />
        </div>
    );
}

export default AddNewUserPage;
